'use client';

import { useEffect, useRef, useState } from 'react';

import { notFound } from 'next/navigation';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import type { FeatureFlag } from '@bridge/constants';

import { Body } from '../typography/Body';
import { useIsLaunchDarklyDisabled } from './launchdarkly-disabled-context';
import { useFeatureFlag } from './useFeatureFlag';

import type { LDContext } from 'launchdarkly-react-client-sdk';

interface FeatureFlagGateProps {
  /** The flag that must be enabled for `children` to render; otherwise renders `notFound()`. */
  flag: FeatureFlag;
  /** When provided, the gate stays pending until the LD client evaluates flags for this context. */
  context?: LDContext;
  /** Maximum time to wait for LaunchDarkly readiness before showing the unavailable fallback. */
  readinessTimeoutMs?: number;
  /** Content shown when LaunchDarkly does not become ready before the timeout. */
  unavailableFallback?: React.ReactNode;
  children: React.ReactNode;
}

type GateStatus = 'pending' | 'ready' | 'unavailable';

interface GateState {
  contextKey?: string;
  contextKind?: string;
  status: GateStatus;
}

function getContextKey(context: LDContext | undefined) {
  return typeof context?.key === 'string' ? context.key : undefined;
}

function getContextKind(context: LDContext | undefined) {
  return context && 'kind' in context && typeof context.kind === 'string' ? context.kind : undefined;
}

/*
Readiness comes from waitUntilReady, not waitForInitialization, because LDProvider publishes ldClient without flags after its init timeout and waitForInitialization then rejects immediately.
Identity is checked too: soft navigation after login leaves the client evaluating flags for the anonymous context it was initialized with.
A stalled initialization or identify ends in the unavailable fallback after readinessTimeoutMs rather than null or a false 404.
*/
export function FeatureFlagGate({
  flag,
  context,
  readinessTimeoutMs = 10000,
  unavailableFallback,
  children,
}: FeatureFlagGateProps) {
  const enabled = useFeatureFlag(flag);
  const ldClient = useLDClient();
  const isLaunchDarklyDisabled = useIsLaunchDarklyDisabled();
  const contextRef = useRef(context);
  const contextKey = getContextKey(context);
  const contextKind = getContextKind(context);
  const expectedContextKey = isLaunchDarklyDisabled ? undefined : contextKey;
  const expectedContextKind = isLaunchDarklyDisabled ? undefined : contextKind;
  const [gateState, setGateState] = useState<GateState>({ status: 'pending' });

  contextRef.current = context;

  useEffect(() => {
    if (isLaunchDarklyDisabled) {
      setGateState({ status: 'ready' });
      return;
    }

    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;

      setGateState((currentState) => {
        const isCurrentContext =
          currentState.contextKey === expectedContextKey && currentState.contextKind === expectedContextKind;
        return !isCurrentContext || currentState.status === 'pending'
          ? { contextKey: expectedContextKey, contextKind: expectedContextKind, status: 'unavailable' }
          : currentState;
      });
    }, readinessTimeoutMs);

    const transition = (status: GateStatus) => {
      if (cancelled) return;

      clearTimeout(timeoutId);
      setGateState((currentState) => {
        const isCurrentContext =
          currentState.contextKey === expectedContextKey && currentState.contextKind === expectedContextKind;
        return !isCurrentContext || currentState.status === 'pending'
          ? { contextKey: expectedContextKey, contextKind: expectedContextKind, status }
          : currentState;
      });
    };

    if (ldClient) {
      ldClient.waitUntilReady().then(() => {
        if (cancelled) return;

        const expectedContext = contextRef.current;
        if (expectedContext) {
          const currentContext = ldClient.getContext();
          if (
            getContextKind(currentContext) !== expectedContextKind ||
            getContextKey(currentContext) !== expectedContextKey
          ) {
            ldClient.identify(expectedContext).then(() => transition('ready'), () => transition('unavailable'));
            return;
          }
        }

        transition('ready');
      });
    }

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [expectedContextKey, expectedContextKind, isLaunchDarklyDisabled, ldClient, readinessTimeoutMs]);

  const isCurrentContext =
    gateState.contextKey === expectedContextKey && gateState.contextKind === expectedContextKind;

  if (!isCurrentContext || gateState.status === 'pending') {
    return null;
  }

  if (gateState.status === 'unavailable') {
    return unavailableFallback ?? <Body size="md">This page is temporarily unavailable. Please try again shortly.</Body>;
  }

  if (!enabled) {
    notFound();
  }

  return <>{children}</>;
}
